import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, LandPlot, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

type Cat = 'homes' | 'plots' | 'commercial';
type Sub = 'popular' | 'types' | 'locations' | 'area';

const PRESETS: Record<Cat, Record<Sub, { label: string; sub: string; href: string }[]>> = {
  homes: {
    popular: [
      { label: '2 Bedroom', sub: 'Apartments', href: '/buy?type=apartment&beds=2' },
      { label: '3 Bedroom', sub: 'Apartments', href: '/buy?type=apartment&beds=3' },
      { label: 'Low Price', sub: 'Houses', href: '/buy?type=villa&maxPrice=5000000' },
      { label: 'New', sub: 'Houses', href: '/buy?type=villa&sort=newest' },
      { label: 'Furnished', sub: 'Apartments', href: '/buy?type=apartment&furnished=1' },
      { label: 'With Parking', sub: 'Houses', href: '/buy?type=villa&parking=1' },
    ],
    types: [
      { label: 'Apartments', sub: 'For Sale', href: '/buy?type=apartment' },
      { label: 'Villas', sub: 'For Sale', href: '/buy?type=villa' },
      { label: 'Penthouses', sub: 'For Sale', href: '/buy?type=penthouse' },
      { label: 'Flats', sub: 'For Sale', href: '/buy?type=flat' },
      { label: 'Rooms', sub: 'For Rent', href: '/rent?type=room' },
      { label: 'Houses', sub: 'For Rent', href: '/rent?type=villa' },
    ],
    locations: [
      { label: 'Addis Ababa', sub: 'Homes', href: '/buy?city=Addis%20Ababa' },
      { label: 'Adama', sub: 'Homes', href: '/buy?city=Adama' },
      { label: 'Hawassa', sub: 'Homes', href: '/buy?city=Hawassa' },
      { label: 'Bahir Dar', sub: 'Homes', href: '/buy?city=Bahir%20Dar' },
      { label: 'Mekelle', sub: 'Homes', href: '/buy?city=Mekelle' },
      { label: 'Dire Dawa', sub: 'Homes', href: '/buy?city=Dire%20Dawa' },
    ],
    area: [
      { label: '<100', sub: 'm²', href: '/buy?maxArea=100' },
      { label: '100-200', sub: 'm²', href: '/buy?minArea=100&maxArea=200' },
      { label: '200-400', sub: 'm²', href: '/buy?minArea=200&maxArea=400' },
      { label: '400-600', sub: 'm²', href: '/buy?minArea=400&maxArea=600' },
      { label: '600+', sub: 'm²', href: '/buy?minArea=600' },
      { label: 'Any', sub: 'Size', href: '/buy' },
    ],
  },
  plots: {
    popular: [
      { label: '200 m²', sub: 'Plots', href: '/plots?maxArea=200' },
      { label: '500 m²', sub: 'Plots', href: '/plots?minArea=400&maxArea=500' },
      { label: 'Corner', sub: 'Plots', href: '/plots?corner=1' },
      { label: 'Park Face', sub: 'Plots', href: '/plots?park=1' },
      { label: 'Main Road', sub: 'Plots', href: '/plots?main=1' },
      { label: 'Low Price', sub: 'Plots', href: '/plots?sort=price_asc' },
    ],
    types: [
      { label: 'Residential', sub: 'Plots', href: '/plots?purpose=residential' },
      { label: 'Commercial', sub: 'Plots', href: '/plots?purpose=commercial' },
      { label: 'Agricultural', sub: 'Plots', href: '/plots?purpose=agricultural' },
      { label: 'Industrial', sub: 'Plots', href: '/plots?purpose=industrial' },
      { label: 'Mixed', sub: 'Plots', href: '/plots?purpose=mixed' },
      { label: 'Any', sub: 'Type', href: '/plots' },
    ],
    locations: [
      { label: 'Addis Ababa', sub: 'Plots', href: '/plots?city=Addis%20Ababa' },
      { label: 'Adama', sub: 'Plots', href: '/plots?city=Adama' },
      { label: 'Hawassa', sub: 'Plots', href: '/plots?city=Hawassa' },
      { label: 'Bahir Dar', sub: 'Plots', href: '/plots?city=Bahir%20Dar' },
      { label: 'Mekelle', sub: 'Plots', href: '/plots?city=Mekelle' },
      { label: 'Dire Dawa', sub: 'Plots', href: '/plots?city=Dire%20Dawa' },
    ],
    area: [
      { label: '<200', sub: 'm²', href: '/plots?maxArea=200' },
      { label: '200-500', sub: 'm²', href: '/plots?minArea=200&maxArea=500' },
      { label: '500-1000', sub: 'm²', href: '/plots?minArea=500&maxArea=1000' },
      { label: '1-5k', sub: 'm²', href: '/plots?minArea=1000&maxArea=5000' },
      { label: '5k+', sub: 'm²', href: '/plots?minArea=5000' },
      { label: 'Any', sub: 'Size', href: '/plots' },
    ],
  },
  commercial: {
    popular: [
      { label: 'Offices', sub: 'For Rent', href: '/commercial?type=office' },
      { label: 'Shops', sub: 'For Rent', href: '/commercial?type=retail' },
      { label: 'Warehouses', sub: 'For Rent', href: '/commercial?type=warehouse' },
      { label: 'Buildings', sub: 'For Sale', href: '/commercial?type=building' },
      { label: 'Showrooms', sub: 'For Rent', href: '/commercial?type=showroom' },
      { label: 'Hotels', sub: 'For Sale', href: '/commercial?type=hotel' },
    ],
    types: [
      { label: 'Office', sub: 'Spaces', href: '/commercial?type=office' },
      { label: 'Retail', sub: 'Shops', href: '/commercial?type=retail' },
      { label: 'Warehouse', sub: 'Storage', href: '/commercial?type=warehouse' },
      { label: 'Building', sub: 'Whole', href: '/commercial?type=building' },
      { label: 'Showroom', sub: 'Display', href: '/commercial?type=showroom' },
      { label: 'Other', sub: 'Types', href: '/commercial' },
    ],
    locations: [
      { label: 'Addis Ababa', sub: 'Commercial', href: '/commercial?city=Addis%20Ababa' },
      { label: 'Adama', sub: 'Commercial', href: '/commercial?city=Adama' },
      { label: 'Hawassa', sub: 'Commercial', href: '/commercial?city=Hawassa' },
      { label: 'Bahir Dar', sub: 'Commercial', href: '/commercial?city=Bahir%20Dar' },
      { label: 'Mekelle', sub: 'Commercial', href: '/commercial?city=Mekelle' },
      { label: 'Dire Dawa', sub: 'Commercial', href: '/commercial?city=Dire%20Dawa' },
    ],
    area: [
      { label: '<50', sub: 'm²', href: '/commercial?maxArea=50' },
      { label: '50-100', sub: 'm²', href: '/commercial?minArea=50&maxArea=100' },
      { label: '100-300', sub: 'm²', href: '/commercial?minArea=100&maxArea=300' },
      { label: '300-1000', sub: 'm²', href: '/commercial?minArea=300&maxArea=1000' },
      { label: '1000+', sub: 'm²', href: '/commercial?minArea=1000' },
      { label: 'Any', sub: 'Size', href: '/commercial' },
    ],
  },
};

const CATEGORIES: { id: Cat; label: string; icon: any }[] = [
  { id: 'homes', label: 'Homes', icon: Home },
  { id: 'plots', label: 'Plots', icon: LandPlot },
  { id: 'commercial', label: 'Commercial', icon: Building2 },
];

const SUBS: { id: Sub; label: string }[] = [
  { id: 'popular', label: 'Popular' },
  { id: 'types', label: 'Types' },
  { id: 'locations', label: 'Locations' },
  { id: 'area', label: 'Area Sizes' },
];

export function MobileBrowsePropertiesCard() {
  const navigate = useNavigate();
  const [cat, setCat] = useState<Cat>('homes');
  const [sub, setSub] = useState<Sub>('popular');
  const items = PRESETS[cat][sub];
  const pageA = items.slice(0, 6);

  return (
    <section className="px-3 pt-4">
      <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
        <div className="px-4 pt-4">
          <h2 className="text-lg font-bold mb-3">Browse Properties</h2>
          <div className="flex gap-5 border-b">
            {CATEGORIES.map(c => {
              const Icon = c.icon;
              const active = cat === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setCat(c.id)}
                  className={cn(
                    'flex items-center gap-1.5 pb-2 -mb-px text-sm font-semibold border-b-2 transition-colors',
                    active ? 'text-primary border-primary' : 'text-muted-foreground border-transparent'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-4 pt-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-2">
            {SUBS.map(s => {
              const active = sub === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSub(s.id)}
                  className={cn(
                    'shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                    active ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
                  )}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-3 pb-4">
          <div className="grid grid-cols-3 gap-2">
            {pageA.map(item => (
              <button
                key={item.label + item.sub}
                onClick={() => navigate(item.href)}
                className="flex flex-col items-center justify-center text-center rounded-lg border bg-background py-3 px-1 hover:border-primary active:bg-muted transition-colors min-h-[64px]"
              >
                <span className="text-sm font-semibold leading-tight">{item.label}</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">{item.sub}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
